import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Presentation, FileUp, Loader2, Wand2, Save, Printer, Image as ImageIcon, Palette, RotateCw, QrCode, Sparkles, LayoutTemplate, MessageSquare, Send, Users, Stethoscope, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Header } from '../components/Header';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { PosterRenderer } from '../components/PosterRenderer';
import { LogoUpload } from '../components/LogoUpload';
import { ArtworkPanel } from '../components/ArtworkPanel';
import { posterSizes, getSize } from '../data/posterSizes';
import { colorSchemes } from '../data/catalog';
import { printPoster, downloadPosterPng } from '../lib/posterExport';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import api from '../lib/api';

const QUICK_EDITS = ['Make it less wordy', 'Add a Red Flags section', 'Add a Do / Avoid comparison', 'Make the stats more prominent', 'Add a step-by-step procedure'];

export default function PosterStudio() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const posterRef = useRef(null);
  const scaleWrapRef = useRef(null);
  const chatEndRef = useRef(null);

  const [sizeId, setSizeId] = useState('18x24');
  const [portrait, setPortrait] = useState(true);
  const [brief, setBrief] = useState('');
  const [topic, setTopic] = useState('');
  const [content, setContent] = useState('');
  const [showSource, setShowSource] = useState(false);
  const [fileName, setFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [density, setDensity] = useState('rich');
  const [accent, setAccent] = useState('#0D9488');
  const [logoState, setLogoState] = useState({ logo: '', logoOriginal: '', logoTransparent: false });
  const [facilityName, setFacilityName] = useState('');
  const [artState, setArtState] = useState({ art: '', artSubject: '' });
  const [qrUrl, setQrUrl] = useState('');
  const [qrLabel, setQrLabel] = useState('Scan for the full policy');
  const [gen, setGen] = useState(false);
  const [poster, setPoster] = useState(null);
  const [patientPoster, setPatientPoster] = useState(null);
  const [view, setView] = useState('staff');
  const [genPatient, setGenPatient] = useState(false);
  const [chat, setChat] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [refining, setRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [busy, setBusy] = useState('');
  const [scale, setScale] = useState(0.3);
  const [brandApplied, setBrandApplied] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);

  // Reopen a saved poster from the Library
  useEffect(() => {
    const id = params.get('id');
    if (!id) return;
    setBrandApplied(true); setLoadingDoc(true);
    api.get(`/onepagers/${id}`).then((r) => {
      const d = r.data?.data || {};
      if (d.poster) setPoster(d.poster);
      if (d.patientPoster) setPatientPoster(d.patientPoster);
      if (d.sizeId) setSizeId(d.sizeId);
      if (typeof d.portrait === 'boolean') setPortrait(d.portrait);
      setLogoState({ logo: d.logo || '', logoOriginal: d.logoOriginal || d.logo || '', logoTransparent: !!d.logoTransparent });
      setFacilityName(d.facilityName || '');
      setArtState({ art: d.art || '', artSubject: d.artSubject || '' });
      setQrUrl(d.qr?.url || ''); if (d.qr?.label) setQrLabel(d.qr.label);
      setBrief(d.brief || ''); setTopic(d.topic || r.data.title || ''); setContent(d.content || '');
      setChat(d.chat || []);
      setAccent(r.data.colorScheme?.primary || d.poster?.accent || '#0D9488');
      setDocId(id);
    }).catch(() => toast({ title: 'Could not open poster', variant: 'destructive' })).finally(() => setLoadingDoc(false));
  }, [params, toast]);

  useEffect(() => {
    if (!brandApplied && brand?.loaded) {
      if (brand.primaryColor) setAccent(brand.primaryColor);
      if (brand.logo) setLogoState({ logo: brand.logo, logoOriginal: brand.logoOriginal || brand.logo, logoTransparent: !!brand.logoTransparent });
      if (brand.facilityName) setFacilityName(brand.facilityName);
      setBrandApplied(true);
    }
  }, [brand, brandApplied]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  const baseSize = getSize(sizeId);
  const size = portrait ? baseSize : { ...baseSize, w: baseSize.h, h: baseSize.w };
  const pxW = size.w * 96;

  useEffect(() => {
    const update = () => { if (scaleWrapRef.current) setScale(Math.min(1, scaleWrapRef.current.offsetWidth / pxW)); };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [pxW, poster]);

  const active = view === 'patient' && patientPoster ? patientPoster : poster;
  const qr = qrUrl ? { url: qrUrl, label: qrLabel } : undefined;
  const posterData = active ? { ...active, accent, qr } : null;
  const brandSwatches = (brand?.colors?.length ? brand.colors : colorSchemes.map((c) => c.primary));
  const logo = logoState.logo;

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setExtracting(true); setFileName(file.name);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/extract-text', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContent(r.data.text); setShowSource(true);
      if (!topic) setTopic(file.name.replace(/\.[^.]+$/, ''));
      toast({ title: 'File imported', description: `${r.data.chars.toLocaleString()} characters extracted.` });
    } catch (err) { toast({ title: 'Could not read file', description: err?.response?.data?.detail || 'Try pasting text.', variant: 'destructive' }); }
    finally { setExtracting(false); }
  };

  const generate = async () => {
    if (!brief.trim() && !topic.trim() && !content.trim()) { toast({ title: 'Describe the poster you want first', variant: 'destructive' }); return; }
    setGen(true);
    try {
      const r = await api.post('/generate-poster', { topic, content, brief, audience: 'staff', density });
      setPoster(r.data.data); setPatientPoster(null); setView('staff'); setChat([]);
      if (r.data.data.accent && !brand?.primaryColor) setAccent(r.data.data.accent);
    } catch (e) { toast({ title: 'Generation failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setGen(false); }
  };

  const refine = async (text) => {
    const msg = (text ?? instruction).trim();
    if (!msg || !active) return;
    setInstruction(''); setRefining(true);
    setChat((c) => [...c, { role: 'user', text: msg, target: view }]);
    try {
      const r = await api.post('/refine-poster', { poster: active, instruction: msg, audience: view === 'patient' ? 'patient' : 'staff', history: chat.filter((m) => m.role === 'user' && m.target === view).map((m) => m.text) });
      if (view === 'patient' && patientPoster) setPatientPoster(r.data.data); else setPoster(r.data.data);
      setChat((c) => [...c, { role: 'ai', text: `Done — updated the ${view === 'patient' ? 'patient' : 'staff'} poster.`, target: view }]);
    } catch (e) {
      setChat((c) => [...c, { role: 'ai', text: `Sorry, that change failed. ${e?.response?.data?.detail || 'Please try again.'}`, target: view, error: true }]);
    } finally { setRefining(false); }
  };

  const makePatientVersion = async () => {
    if (!poster) return; setGenPatient(true);
    try {
      const r = await api.post('/poster-patient-version', { poster });
      setPatientPoster(r.data.data); setView('patient');
      toast({ title: 'Patient-facing version ready', description: 'Switch between Staff and Patient tabs above the preview.' });
    } catch (e) { toast({ title: 'Could not create patient version', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setGenPatient(false); }
  };

  const exportPng = async () => {
    setBusy('png');
    try { await downloadPosterPng(posterRef.current, `${(active.title || 'poster').replace(/\s+/g, '_')}_${view}_${size.w}x${size.h}`); toast({ title: 'PNG downloaded' }); }
    catch { toast({ title: 'PNG export failed', variant: 'destructive' }); } finally { setBusy(''); }
  };

  const save = async () => {
    if (!user) return; setSaving(true);
    try {
      const payload = {
        title: poster.title || topic || 'Poster', subtitle: patientPoster ? `${poster.subtitle || ''} · Staff + Patient series`.trim() : poster.subtitle, docType: 'poster', layout: 'poster-studio',
        data: { poster: { ...poster, accent }, patientPoster: patientPoster ? { ...patientPoster, accent } : null, sizeId, portrait, logo: logoState.logo, logoOriginal: logoState.logoOriginal, logoTransparent: logoState.logoTransparent, facilityName, art: artState.art, artSubject: artState.artSubject, qr, brief, topic, content, chat, source: content ? (fileName || 'imported') : '' },
        colorScheme: { primary: accent, background: '#f8fafc' },
      };
      const r = docId ? await api.put(`/onepagers/${docId}`, payload) : await api.post('/onepagers', payload);
      setDocId(r.data.id); toast({ title: docId ? 'Poster updated' : 'Saved to library!' });
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  const Swatch = ({ c }) => (
    <button onClick={() => setAccent(c)} className={`h-8 w-8 rounded-lg border-2 transition-transform hover:scale-110 ${accent?.toLowerCase() === c?.toLowerCase() ? 'border-gray-900 ring-2 ring-offset-1 ring-gray-300' : 'border-white shadow'}`} style={{ backgroundColor: c }} title={c} />
  );

  return (
    <>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative overflow-hidden rounded-3xl mb-6 p-6 sm:p-8" style={{ background: 'linear-gradient(120deg,#0d9488 0%,#0891b2 45%,#7c3aed 100%)' }}>
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <button onClick={() => navigate('/')} className="relative text-white/80 hover:text-white text-sm flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shadow-lg"><Presentation className="w-7 h-7 text-white" /></div>
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold mb-1"><Sparkles className="w-3.5 h-3.5" /> AI Poster Studio</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Tell it what you want. Get a poster.</h1>
              <p className="text-white/85 text-sm mt-0.5">Describe it in plain words · refine by chat · patient version in one click · your brand, any size</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[400px_1fr] gap-6">
          <div className="space-y-4">
            <Card className="border-teal-100 shadow-sm"><CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-teal-700"><Wand2 className="w-4 h-4" /> Describe your poster</div>
              <Textarea rows={5} value={brief} onChange={(e) => setBrief(e.target.value)} data-testid="poster-brief-input" placeholder={'e.g. "A bold 24x36 poster for ICU nurses on CIWA-Ar alcohol withdrawal: when to score, the 10 items, severity bands with colors, red flags to escalate, and a do/avoid list. Authoritative but friendly."'} className="resize-none text-sm leading-relaxed" />
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                <div><Label className="text-xs">Title / topic (optional)</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="poster-topic-input" placeholder="CIWA-Ar Alcohol Withdrawal" /></div>
                <div>
                  <Label className="text-xs">Detail</Label>
                  <select value={density} onChange={(e) => setDensity(e.target.value)} className="border rounded-lg px-2 h-10 text-sm" data-testid="poster-density-select"><option value="rich">Rich</option><option value="concise">Concise</option></select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Poster Size</Label>
                <div className="flex gap-2">
                  <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="poster-size-select">
                    {posterSizes.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.w}×{s.h}in)</option>)}
                  </select>
                  <Button variant="outline" size="icon" onClick={() => setPortrait((v) => !v)} title="Rotate" data-testid="poster-rotate-btn"><RotateCw className="w-4 h-4" /></Button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">{portrait ? 'Portrait' : 'Landscape'} · {size.w}in × {size.h}in</p>
              </div>
              <button onClick={() => setShowSource((v) => !v)} className="w-full flex items-center justify-between text-xs font-medium text-gray-600 hover:text-teal-700" data-testid="poster-source-toggle"><span className="flex items-center gap-1"><FileUp className="w-3.5 h-3.5" /> Import a PowerPoint / PDF / Word or paste text{content ? ` · ${content.length.toLocaleString()} chars` : ''}</span>{showSource ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
              {showSource && (
                <div className="space-y-2">
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-teal-300 rounded-xl py-4 cursor-pointer hover:bg-teal-50/60 text-sm text-gray-600 transition-colors">
                    {extracting ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" /> : <FileUp className="w-4 h-4 text-teal-600" />}
                    {extracting ? 'Reading...' : fileName || 'Choose a file'}
                    <input type="file" accept=".pptx,.pdf,.docx,.txt,.md" className="hidden" onChange={onFile} data-testid="poster-file-input" />
                  </label>
                  <Textarea rows={4} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste policy, slide notes, or guideline text..." className="resize-none text-xs" data-testid="poster-content-input" />
                </div>
              )}
              <Button onClick={generate} disabled={gen} data-testid="poster-generate-btn" className="w-full h-11 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-600 hover:from-teal-600 hover:to-cyan-700 text-white shadow-md">{gen ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Designing poster...</> : <><Wand2 className="w-4 h-4 mr-1" /> {poster ? 'Start over with a new poster' : 'Generate Poster'}</>}</Button>
            </CardContent></Card>

            {poster && (
              <>
                <Card className="border-indigo-100 shadow-sm"><CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2 text-sm font-semibold text-indigo-700"><MessageSquare className="w-4 h-4" /> Refine by chat</div><span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium capitalize">editing {view} poster</span></div>
                  <div className="max-h-48 overflow-y-auto space-y-2 pr-1" data-testid="poster-chat-log">
                    {chat.length === 0 && <p className="text-xs text-gray-400">Tell it what to change — add or remove sections, shorten text, change emphasis, fix facts. It applies the edit and keeps the rest.</p>}
                    {chat.map((m, i) => (
                      <div key={i} className={`text-xs rounded-xl px-3 py-2 ${m.role === 'user' ? 'bg-indigo-600 text-white ml-6' : m.error ? 'bg-red-50 text-red-700 mr-6' : 'bg-gray-100 text-gray-700 mr-6'}`}>{m.text}</div>
                    ))}
                    {refining && <div className="text-xs rounded-xl px-3 py-2 bg-gray-100 text-gray-500 mr-6 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Applying your change...</div>}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="flex flex-wrap gap-1">{QUICK_EDITS.map((q) => <button key={q} onClick={() => refine(q)} disabled={refining} className="text-[11px] px-2 py-1 rounded-full border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">{q}</button>)}</div>
                  <div className="flex gap-2">
                    <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !refining && refine()} placeholder="e.g. Add a section on nursing documentation" className="text-sm" data-testid="poster-refine-input" />
                    <Button onClick={() => refine()} disabled={refining || !instruction.trim()} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" size="icon" data-testid="poster-refine-send-btn">{refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
                  </div>
                </CardContent></Card>

                <Card className="border-purple-100"><CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-purple-700"><Palette className="w-4 h-4" /> Brand & Style</div>
                  <div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent Color</Label><input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-7 h-7 rounded border cursor-pointer p-0" title="Custom color" data-testid="poster-accent-picker" /></div>
                    <div className="flex flex-wrap gap-2 mt-2">{brandSwatches.map((c, i) => <Swatch key={i} c={c} />)}</div>
                    {brand?.colors?.length > 0 && <p className="text-[10px] text-gray-400 mt-1">Showing your saved workspace brand colors.</p>}
                  </div>
                  <div className="grid grid-cols-[1fr_1fr] gap-3 items-start">
                    <div><Label className="text-xs">Logo</Label><div className="mt-1"><LogoUpload compact value={logoState} onChange={setLogoState} /></div></div>
                    <div><Label className="text-xs">Facility Name</Label><Input value={facilityName} onChange={(e) => setFacilityName(e.target.value)} className="h-9 text-xs mt-1" data-testid="poster-facility-input" /></div>
                  </div>
                  <ArtworkPanel value={artState} onChange={(p) => setArtState((s) => ({ ...s, ...p }))} kind="illustration" accent={accent} piece="poster" aspect="square" defaultSubject={`hero illustration for a poster about ${active?.title || topic || 'this topic'}`} hint="Adds a hero illustration to the poster header, matched to the topic." />
                </CardContent></Card>

                <Card className="border-cyan-100"><CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-cyan-700"><QrCode className="w-4 h-4" /> QR Code (optional)</div>
                  <Input value={qrUrl} onChange={(e) => setQrUrl(e.target.value)} placeholder="https://link-to-full-policy-or-elearning" className="text-sm" data-testid="poster-qr-url-input" />
                  {qrUrl && <Input value={qrLabel} onChange={(e) => setQrLabel(e.target.value)} placeholder="Label (e.g. Scan for the full policy)" className="text-sm" />}
                </CardContent></Card>

                <Card className="border-teal-100"><CardContent className="p-4 space-y-2">
                  <Button onClick={() => printPoster(posterRef.current, size.w, size.h)} variant="outline" className="w-full" data-testid="poster-print-btn"><Printer className="w-4 h-4 mr-1" /> Print / Save as PDF</Button>
                  <Button onClick={exportPng} disabled={busy === 'png'} variant="outline" className="w-full" data-testid="poster-png-btn">{busy === 'png' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1" />} Download PNG (high-res)</Button>
                  <Button onClick={save} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="poster-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {docId ? 'Update in Library' : 'Save to Library'}</Button>
                  {patientPoster && <p className="text-[11px] text-gray-400 text-center">Export acts on the poster shown; both versions are saved together.</p>}
                </CardContent></Card>
              </>
            )}
          </div>

          <div className="rounded-2xl p-4 overflow-auto" style={{ background: 'radial-gradient(circle at 30% 20%, #e0f2fe, #f1f5f9 60%)', minHeight: '60vh' }}>
            {loadingDoc ? (
              <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
            ) : !poster ? (
              <div className="h-[60vh] flex flex-col items-center justify-center text-center px-6">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-xl mb-4"><Presentation className="w-10 h-10 text-white" /></div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">Your poster will appear here</h3>
                <p className="max-w-sm text-sm text-gray-500">Describe what you want in plain words (or import a PowerPoint/PDF), then hit <b className="text-teal-600">Generate Poster</b>. Then refine it by chat until it's perfect.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="inline-flex rounded-xl bg-white shadow-sm border p-1" data-testid="poster-view-tabs">
                    <button onClick={() => setView('staff')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${view === 'staff' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-testid="poster-tab-staff"><Stethoscope className="w-3.5 h-3.5" /> Staff</button>
                    <button onClick={() => patientPoster && setView('patient')} disabled={!patientPoster} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${view === 'patient' ? 'bg-purple-600 text-white' : patientPoster ? 'text-gray-600 hover:bg-gray-50' : 'text-gray-300'}`} data-testid="poster-tab-patient"><Users className="w-3.5 h-3.5" /> Patient & Family</button>
                  </div>
                  <Button onClick={makePatientVersion} disabled={genPatient} size="sm" className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow" data-testid="poster-patient-version-btn">{genPatient ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Writing patient version...</> : <><Users className="w-4 h-4 mr-1" /> {patientPoster ? 'Regenerate patient version' : 'Create patient-facing version'}</>}</Button>
                </div>
                <motion.div key={view} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} ref={scaleWrapRef} className="w-full flex justify-center">
                  <div style={{ width: pxW * scale, height: size.h * 96 * scale }}>
                    <div ref={posterRef} style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: pxW, boxShadow: '0 20px 50px rgba(0,0,0,0.18)' }}>
                      <PosterRenderer poster={posterData} size={size} logo={logo} facilityName={facilityName} audience={view} art={artState.art} />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
